const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/components/PDFViewer/DrawingArea.tsx';
import * as React from 'react';
import { forwardRef, useImperativeHandle, useEffect, useRef } from 'react';
import { useSvgDrawing } from 'react-hooks-svgdrawing';
import { makeRGB } from '../../utils/colorUtils';
import { useDidUpdateEffect } from '../../hooks/useDidUpdateEffect';

export type SVGDrawingOptions = {
    penColor?: { r: number; g: number; b: number };
    penWidth?: number;
    delay?: number;
};

const defaultSVGDrawingOptions: SVGDrawingOptions = {
    penColor: { r: 65 / 255, g: 105 / 255, b: 225 / 255 },
    penWidth: 2,
    delay: 5,
};

export const addSVGDrawingOptionsDefaults = (svgDrawingOptions: SVGDrawingOptions = {}): SVGDrawingOptions => ({
    ...defaultSVGDrawingOptions,
    ...svgDrawingOptions,
});

interface Props {
    enabled: boolean;
    baseComponent: HTMLCanvasElement | null;
    stageComponent: HTMLDivElement | null;
    svgDrawingOptions: SVGDrawingOptions;
    scale: number;
    pageNumber: number;
    onChange?: (hasDrawing: boolean) => void;
}

export const DrawingArea = forwardRef((props: Props, ref: any) => {
    const { enabled, baseComponent, scale, stageComponent, onChange } = props;
    const { penColor, penWidth, delay } = props.svgDrawingOptions;
    const drawingPenWidth = (penWidth! * scale) / 100;

    const [drawingRef, action] = useSvgDrawing({
        penColor: makeRGB(penColor!.r * 255, penColor!.g * 255, penColor!.b * 255),
        penWidth: drawingPenWidth,
        delay,
    });

    useDidUpdateEffect(() => {
        action.changePenWidth(drawingPenWidth);
    }, [scale]);

    // Returns true when the SVG contains at least one drawn path.
    const hasPaths = () => {
        const xml = action.getSvgXML();
        return xml !== null && xml.indexOf('<path') > 0;
    };

    // Notify the parent whether any paths exist.
    // After a clear() call the SVG DOM hasn't updated yet, so we wait one animation
    // frame before reading it. After a pointerup the path is already in the DOM, so
    // we can read it immediately.
    const notifyChange = () => onChange?.(hasPaths());
    const notifyChangeNextFrame = () => requestAnimationFrame(notifyChange);

    useImperativeHandle(ref, () => ({
        clear: () => {
            action.clear();
            notifyChangeNextFrame();
        },
        getSvgXml: () => action.getSvgXML(),
        undo: () => {
            // A stroke lands as 1 path (pointer/stylus) or 2 (a finger fires BOTH
            // pointerdown and touchstart, so react-hooks-svgdrawing's _drawingStart runs
            // twice and leaves an empty placeholder path before the real one). Pop the real
            // path, then drain a trailing empty placeholder if there is one — so one undo
            // removes exactly one visible stroke on every device, without guessing from a
            // device flag. (The lib exposes no stroke lifecycle events to do this cleanly.)
            const lastPathIsEmptyPlaceholder = () => {
                const paths = action.instance?.paths ?? [];
                const lastPath = paths[paths.length - 1];
                return lastPath !== undefined && lastPath.commands.length === 0;
            };

            action.undo();
            while (lastPathIsEmptyPlaceholder()) {
                action.undo();
            }
            notifyChangeNextFrame();
        },
    }));

    useEffect(() => {
        const el = drawingRef.current as HTMLElement | null;
        if (!el) return;

        // MutationObserver is more reliable than pointerup for detecting path changes.
        // react-hooks-svgdrawing writes path segments on a 5ms timer (the `delay` option),
        // so pointerup fires before the paths land in the DOM. An observer reacts to the
        // actual DOM mutation regardless of timing.
        const observer = new MutationObserver(notifyChange);
        observer.observe(el, { childList: true, subtree: true });
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const el = drawingRef.current as HTMLElement | null;
        if (!el) return;

        // svg-drawing's handleTouch wrapper is used for touchend too, but on touchend
        // ev.touches is already empty (finger lifted) → ev.touches[0].clientX crashes.
        // Intercept in capture phase before the library's bubble-phase handler fires.
        // The scroll container's capture listeners are hitigher in the tree and already
        // run before this, so pinch-zoom state is unaffected.
        const preventTouchEndCrash = (e: TouchEvent) => {
            if (e.touches.length === 0) {
                e.stopImmediatePropagation();
            }
        };

        el.addEventListener('touchend', preventTouchEndCrash, { capture: true });
        return () => el.removeEventListener('touchend', preventTouchEndCrash, { capture: true });
    }, []);

    const style: any = {
        backgroundColor: 'transparent',
        position: 'absolute',
        zIndex: 3,
    };

    if (!enabled || !baseComponent || !stageComponent) {
        style.display = 'none';
    }

    if (baseComponent && stageComponent) {
        const baseRect = baseComponent.getBoundingClientRect();
        const stageRect = stageComponent.getBoundingClientRect();
        style.left = baseRect.left - stageRect.left;
        style.top = baseRect.top - stageRect.top;
        style.width = baseRect.width;
        style.height = baseRect.height;
    }

    return <div ref={drawingRef} style={style} />;
});

DrawingArea.displayName = 'DrawingArea';
